import { memoryStore, useStore } from "../../../lib/store";
import type { LegalDocTab } from "./settingsSheetStore";

/** The Account row being edited in place (Figma "account.change-name", …). */
export type AccountField = "firstName" | "lastName" | "bio" | "link";

/**
 * The page the desktop settings modal shows (Figma "06 - Settings & compte").
 *
 * Most are an entry of the modal's navigation. The others open from a row of
 * one of them (Account › Change photo, Account › Delete…, Partner sharing ›
 * See our partners, a legal link) and carry a way back to it. Nothing opens
 * over the modal: an edit or a confirmation happens in the row or on a page
 * of the modal, so the page carries which row is open.
 */
export type DesktopSettingsPage =
  | { page: "account"; editing: AccountField | null }
  | { page: "avatar" }
  | { page: "deleteAccount" }
  | { page: "deleteConversations" }
  | { page: "specialty" }
  | { page: "language" }
  | { page: "notifications" }
  | { page: "partnerSharing" }
  | { page: "partners" }
  | { page: "practice" }
  | { page: "interests" }
  | { page: "rooms"; leaving: string | null }
  | { page: "certification" }
  | { page: "terms"; tab: LegalDocTab }
  | { page: "privacy"; tab: LegalDocTab }
  // Privacy policy › Partners › a partner of a room the reader joined.
  | { page: "partnerPrivacy"; organisationId: string };

type DesktopSettingsState =
  { status: "closed" } | { status: "open"; page: DesktopSettingsPage };

// Module-level singleton, like the mobile settings sheet: the modal is mounted
// once by the desktop shell and opened from anywhere (the account menu, the
// shortcut, the profile, the `/settings/*` addresses in emails).
const store = memoryStore<DesktopSettingsState>({ status: "closed" });

export const ACCOUNT_PAGE: DesktopSettingsPage = {
  page: "account",
  editing: null,
};

export function useDesktopSettings(): DesktopSettingsState {
  const [state] = useStore(store);
  return state;
}

export function openDesktopSettings(page: DesktopSettingsPage): void {
  store.set({ status: "open", page });
}

export function closeDesktopSettings(): void {
  store.set({ status: "closed" });
}

function isSamePage(a: DesktopSettingsPage, b: DesktopSettingsPage): boolean {
  switch (a.page) {
    case "account":
      return b.page === "account" && b.editing === a.editing;
    case "rooms":
      return b.page === "rooms" && b.leaving === a.leaving;
    case "terms":
    case "privacy":
      return b.page === a.page && b.tab === a.tab;
    case "partnerPrivacy":
      return (
        b.page === "partnerPrivacy" && b.organisationId === a.organisationId
      );
    case "avatar":
    case "deleteAccount":
    case "deleteConversations":
    case "specialty":
    case "language":
    case "notifications":
    case "partnerSharing":
    case "partners":
    case "practice":
    case "interests":
    case "certification":
      return b.page === a.page;
    default:
      throw new Error(a satisfies never);
  }
}

/**
 * The end of an awaited save: moves from the page that started it to `to`, and
 * only from there. By then the reader may have closed the modal, or opened
 * another page or another row (a draft in the Bio editor while the first name
 * saves): both stay as they are.
 */
export function leaveDesktopSettingsPage(
  from: DesktopSettingsPage,
  to: DesktopSettingsPage
): void {
  store.set((prev) =>
    prev.status === "open" && isSamePage(prev.page, from)
      ? { status: "open", page: to }
      : prev
  );
}
